import type {ReactElement} from 'react';
import React, {useMemo, useState} from 'react';
import {ContextualMenu} from '@Components/contextual-menu';
import {ItemHeaderTab} from '@Components/poster-editor/components/poster-editing-side-panel/poster-editing-side-panel.types';
import {ItemPositionContextualOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-position-contextual-options';
import {PosterItemControls} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-item-controls';
import {ItemOpacityOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-opacity-option';
import {ContextualMenuHeadingOption} from '@Components/contextual-menu/components/contextual-menu-heading-option';
import {ItemInteractivityLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-interactivity-link-option';
import {InformationLinkOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/information-link-option';
import {ItemSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-size-option';
import {ItemLetterSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-letter-spacing-option';
import {ItemLineHeightOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-line-height-option';
import {ItemBorderOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-option';
import {TableLayoutOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/table-layout-option';
import {TablePanels} from '@Components/poster-editor/components/poster-editing-side-panel/components/poster-editing-table-options/poster-editing-table-options.types';
import {ContextualMenuHeader} from '@Components/contextual-menu/components/contextual-menu-header';
import {ContextualMenuTextHeader} from '@Components/contextual-menu/components/contextual-menu-text-header';
import {TableLayoutSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/table-layout-selector';
import {LayoutBackgroundOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-option';
import {ItemVerticalSpacingOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-vertical-spacing-option';
import {LayoutBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-background-color-panel';
import {ItemBorderColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-border-color-panel';
import {TableHorizontalSpacing} from '@Components/poster-editor/components/poster-editing-side-panel/components/table-horizontal-spacing';
import {TextColorOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-color-option';
import {TextOutlineOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-option';
import {TextFillColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-fill-color-panel';
import {TextOutlineColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/text-outline-color-panel';
import {TableStylingOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/table-styling-options';
import {ScheduleDateOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/schedule-date-options';
import {LayoutHighlightedBackgroundColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-highlighted-background-color-panel';
import {LayoutHighlightedTextColorPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/layout-highlighted-text-color-panel';
import {TableEditContentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/table-edit-content-option';
import {ItemAlignmentOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-alignment-option';
import {ItemLockInformationBox} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-lock-information-box';
import {ItemWithDualFontsSelector} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-fonts-selector';
import {TableHighlightOptions} from '@Components/poster-editor/components/poster-editing-side-panel/components/table-highlight-options';
import {FontFamilyOptionType} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-with-dual-font-options/item-with-dual-font-options.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useIsOpenedTablePanelValid} from '@Hooks/poster-editor/useIsOpenedTablePanelValid';
import {ItemAnimationPanel} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-panel/item-animation-panel';
import {ItemAnimationOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/item-animation-option/item-animation-option';
import {ContextualMenuContentKeys} from '@Components/poster-editor/components/poster-editing-side-panel/components/contextual-options/contextual-options.types';

export function PosterEditingTableOptions(): ReactElement<any> {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const [currentItemHeaderTab, setCurrentItemHeaderTab] = useState(ItemHeaderTab.EDIT);
  const [currentlyOpenedPanel, setCurrentlyOpenedPanel] = useState(TablePanels.NONE);
  useIsOpenedTablePanelValid(currentlyOpenedPanel, setCurrentlyOpenedPanel);

  if (!activeItem) {
    throw new Error('Active item does not exist in table options');
  }

  if (!activeItem.isTableItem()) {
    throw new Error(`Expected item to be of type Table - instead got ${activeItem.gitype}`);
  }

  const getHeaderText = (): string => {
    if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
      return window.i18next.t('pmwjs_position');
    }
    if (currentlyOpenedPanel === TablePanels.ANIMATION) {
      return window.i18next.t('pmwjs_animation');
    } else if (currentlyOpenedPanel === TablePanels.LAYOUT) {
      return window.i18next.t('pmwjs_layout');
    } else {
      return window.i18next.t('pmwjs_color');
    }
  };

  const getHeader = (): ReactElement<any> | undefined => {
    if (showDefaultOptions() && currentItemHeaderTab === ItemHeaderTab.EDIT) {
      return undefined;
    }
    if (isFontSelectorOpened()) {
      return undefined;
    }
    return (
      <ContextualMenuHeader id="table-layout-panel-header">
        <ContextualMenuTextHeader
          text={getHeaderText()}
          showBackButton
          onBackButton={(): void => {
            if (currentItemHeaderTab === ItemHeaderTab.POSITION) {
              setCurrentItemHeaderTab(ItemHeaderTab.EDIT);
            }
            setCurrentlyOpenedPanel(TablePanels.NONE);
          }}
        />
      </ContextualMenuHeader>
    );
  };

  const getPositionOptions = (): ReactElement<any> => {
    return <ItemPositionContextualOptions />;
  };

  const getEditOptions = (): ReactElement<any> => {
    return (
      <>
        <ItemLockInformationBox />
        <PosterItemControls
          onClickPosition={(): void => {
            setCurrentItemHeaderTab(ItemHeaderTab.POSITION);
          }}
        />
        <ItemOpacityOption />
        <ItemAnimationOption
          onAnimationTypeClick={(): void => {
            setCurrentlyOpenedPanel(TablePanels.ANIMATION);
          }}
        />
        <TableEditContentOption />
        <TableLayoutOption
          onClickLayout={(): void => {
            setCurrentlyOpenedPanel(TablePanels.LAYOUT);
          }}
        />
        <ContextualMenuHeadingOption id="table-text-styles-option" text={window.i18next.t('pmwjs_text_styles')} />
        <TableStylingOptions
          onClickFontButton={(clickedFontOption): void => {
            switch (clickedFontOption) {
              case FontFamilyOptionType.ONE:
              case FontFamilyOptionType.ONLY_FONT:
                setCurrentlyOpenedPanel(TablePanels.FONT_ONE);
                break;
              case FontFamilyOptionType.TWO:
                setCurrentlyOpenedPanel(TablePanels.FONT_TWO);
                break;
              default:
            }
          }}
        />
        <ItemSizeOption />
        <TextColorOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TablePanels.FILL);
          }}
        />
        <TextOutlineOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TablePanels.OUTLINE);
          }}
        />
        <ItemAlignmentOption />
        <ItemLineHeightOption />
        <ItemLetterSpacingOption />
        <ContextualMenuHeadingOption id="menu-layout-styles-option" text={window.i18next.t('pmwjs_layout_styles')} />
        <LayoutBackgroundOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TablePanels.BACKGROUND);
          }}
        />
        <ItemBorderOption
          onClickColorFill={(): void => {
            setCurrentlyOpenedPanel(TablePanels.BORDER);
          }}
        />
        <TableHorizontalSpacing />
        <ItemVerticalSpacingOption />
        <ScheduleDateOptions
          onClickHighlightedBackgroundOption={(): void => {
            setCurrentlyOpenedPanel(TablePanels.HIGHLIGHTED_BACKGROUND);
          }}
          onClickHighlightedTextOption={(): void => {
            setCurrentlyOpenedPanel(TablePanels.HIGHLIGHTED_TEXT);
          }}
        />
        <TableHighlightOptions
          onClickHighlightedBackgroundOption={(): void => {
            setCurrentlyOpenedPanel(TablePanels.HIGHLIGHTED_BACKGROUND);
          }}
          onClickHighlightedTextOption={(): void => {
            setCurrentlyOpenedPanel(TablePanels.HIGHLIGHTED_TEXT);
          }}
        />
        <ContextualMenuHeadingOption id="table-fill-interactivity-option" text={window.i18next.t('pmwjs_interactivity')} />
        <ItemInteractivityLinkOption />
        <InformationLinkOption />
      </>
    );
  };

  const showDefaultOptions = (): boolean => {
    return currentlyOpenedPanel === TablePanels.NONE;
  };

  const getPanels = (): ReactElement<any> => {
    switch (currentlyOpenedPanel) {
      case TablePanels.FILL:
        return <TextFillColorPanel isForLayoutItem />;
      case TablePanels.OUTLINE:
        return <TextOutlineColorPanel />;
      case TablePanels.LAYOUT:
        return <TableLayoutSelector />;
      case TablePanels.BACKGROUND:
        return <LayoutBackgroundColorPanel />;
      case TablePanels.BORDER:
        return <ItemBorderColorPanel />;
      case TablePanels.HIGHLIGHTED_BACKGROUND:
        return <LayoutHighlightedBackgroundColorPanel />;
      case TablePanels.HIGHLIGHTED_TEXT:
        return <LayoutHighlightedTextColorPanel />;
      case TablePanels.ANIMATION:
        return <ItemAnimationPanel />;
      case TablePanels.FONT_ONE:
        return (
          <ItemWithDualFontsSelector
            onBack={(): void => {
              setCurrentlyOpenedPanel(TablePanels.NONE);
            }}
          />
        );
      case TablePanels.FONT_TWO:
        return (
          <ItemWithDualFontsSelector
            font={FontFamilyOptionType.TWO}
            onBack={(): void => {
              setCurrentlyOpenedPanel(TablePanels.NONE);
            }}
          />
        );
      default:
        throw new Error(`unhandled case ${currentlyOpenedPanel} for sub panels in table item`);
    }
  };

  const getDefaultOptions = (): ReactElement<any> => {
    switch (currentItemHeaderTab) {
      case ItemHeaderTab.EDIT:
        return getEditOptions();
      case ItemHeaderTab.POSITION:
        return getPositionOptions();
      default:
        throw new Error('unhandled tab item for contextual header');
    }
  };

  const getOptions = (): ReactElement<any> => {
    if (!showDefaultOptions()) {
      return getPanels();
    }

    return getDefaultOptions();
  };

  const isFontSelectorOpened = (): boolean => {
    return currentlyOpenedPanel === TablePanels.FONT_ONE || currentlyOpenedPanel === TablePanels.FONT_TWO;
  };

  const getKey = (): string => {
    if (showDefaultOptions()) {
      return currentItemHeaderTab === ItemHeaderTab.POSITION ? ContextualMenuContentKeys.TABLE_DEFAULT_POSITION : ContextualMenuContentKeys.TABLE_DEFAULT_EDIT;
    }

    return '';
  };

  return useMemo((): ReactElement<any> => {
    return (
      <ContextualMenu
        contentKey={getKey()}
        id="table-contextual-options"
        contentClassName={isFontSelectorOpened() ? 'spacing-p-0' : ''}
        includeLeftRightPadding={showDefaultOptions()}
        header={getHeader()}
      >
        {getOptions()}
      </ContextualMenu>
    );
  }, [currentItemHeaderTab, currentlyOpenedPanel]);
}
